'use client';

import { Check } from 'lucide-react';
import { cn } from '@/shared/lib/cn';

interface PickerRowProps {
  title: string;
  /** Plain text, or a block (e.g. a stacked list of what is selected below). */
  description?: React.ReactNode;
  /**
   * Let the title wrap and the description grow instead of being cut off.
   *
   * Off by default — a catalogue list reads best as even, single-line rows. Turn
   * it on where the row IS the answer to "what did I pick": manufacturers name
   * products at whatever length they like, and an ellipsis there hides the part
   * that tells two picks apart.
   */
  multiline?: boolean;
  /** Optional leading element — thumbnail (44×32), icon, or color-swatch row.
   *  Mirrors Apple `SelectableImageRow` / `SelectableRow<Leading>`. */
  leading?: React.ReactNode;
  /** Optional element rendered next to the title (e.g. PRO badge). */
  trailing?: React.ReactNode;
  /** Optional element pinned to the far right, before the selected checkmark
   *  (e.g. a thumbnail strip on a reference-source row). */
  accessory?: React.ReactNode;
  isSelected?: boolean;
  isDisabled?: boolean;
  onClick?: () => void;
  className?: string;
}

/**
 * Unified picker row for render-settings sub-pickers (Materials, Environment,
 * Style, Output, Model, Colors, ...). Apple parity: button + optional leading
 * (thumbnail / icon / swatches) + title + optional description + trailing
 * checkmark when selected. Lives inside a `SettingsCard` (light-gray grouped
 * surface) and uses a hairline divider between rows.
 *
 * Mirrors `SelectableTextRow` / `SelectableDescriptionRow` /
 * `SelectableImageRow` / `SelectableRow` from
 * `apps/apple/.../UI/Components/SelectableRowView.swift`.
 */
export function PickerRow({
  title,
  description,
  leading,
  trailing,
  accessory,
  multiline = false,
  isSelected = false,
  isDisabled = false,
  onClick,
  className,
}: PickerRowProps) {
  return (
    <button
      type="button"
      disabled={isDisabled}
      onClick={onClick}
      className={cn(
        'w-full flex gap-3 py-3.5 px-4 text-start',
        multiline ? 'items-start' : 'items-center',
        // Visible iOS-style hairline. The `--border` token is oklch(0.94) —
        // nearly invisible on the grouped card fill — so a faded `/40` of it
        // vanished entirely; a tinted muted-foreground reads like the native
        // inset-grouped separator.
        'border-b border-muted-foreground/25 last:border-b-0',
        'transition-colors',
        isDisabled
          ? 'opacity-50 cursor-not-allowed'
          : 'cursor-pointer hover:bg-foreground/5 active:bg-foreground/10',
        className
      )}
    >
      {leading && (
        <div className={cn('flex-shrink-0', multiline && 'mt-0.5')}>
          {leading}
        </div>
      )}

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={cn(
              'text-settings-row-title',
              multiline ? 'break-words' : 'truncate',
              isDisabled ? 'text-muted-foreground' : 'text-foreground'
            )}
          >
            {title}
          </span>
          {trailing && <span className="flex-shrink-0">{trailing}</span>}
        </div>
        {description &&
          (multiline ? (
            <div className="text-settings-row-value text-muted-foreground mt-0.5">
              {description}
            </div>
          ) : (
            <p className="text-settings-row-value text-muted-foreground mt-0.5 line-clamp-2">
              {description}
            </p>
          ))}
      </div>

      {accessory && (
        <div className={cn('flex-shrink-0', multiline && 'mt-0.5')}>
          {accessory}
        </div>
      )}

      {isSelected && (
        <Check
          className={cn(
            'w-5 h-5 text-primary flex-shrink-0',
            multiline && 'mt-0.5'
          )}
          strokeWidth={2.5}
        />
      )}
    </button>
  );
}
